import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { meetingsApi } from '../../../../services/api/meetingsApi';
import { useAlert } from '../../../../contexts/AlertContext';
import EmployeeSearch from '../../../common/EmployeeSearch';
import { EmployeeAvatar } from '../../../common/EmployeeAvatar';
import MeetingStatusSelector from '../../../common/filters/MeetingStatusSelector';
import MeetingTypesSelector from '../../../common/filters/MeetingTypesSelector';
import ConfirmationModal from '../../../common/ConfirmationModal';
import Modal from '../../../common/Modal';
import { HeaderButton } from '../../../common/styles';
import EmptyState from '../../../common/EmptyState';
import { MEETING_ATTENDEE_TYPES } from '../../../../constants/meetingAttendeeTypes';

const GeneralTab = ({
  meeting,
  attendeeTypes,
  permissions,
  onFieldChange,
  onStatusChange,
  onRefresh,
  onChange,
}) => {
  const { showAlert } = useAlert();
  const [showAddAttendee, setShowAddAttendee] = useState(false);
  const [selectedAttendeeType, setSelectedAttendeeType] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [attendeeToRemove, setAttendeeToRemove] = useState(null);

  // Local state for meeting type IDs for optimistic updates
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);

  // Sync local state when meeting data changes
  useEffect(() => {
    const typeIds = meeting.meetingTypes?.map(t => t.meetingType?.id || t.id) || [];
    setSelectedTypeIds(typeIds);
  }, [meeting.meetingTypes]);

  const handleAddAttendee = async () => {
    if (!selectedEmployee || !selectedAttendeeType) return;

    try {
      await meetingsApi.addAttendee(meeting.id, {
        employeeId: selectedEmployee.id,
        attendeeTypeId: selectedAttendeeType,
      });
      showAlert('Attendee added', 'success');
      setShowAddAttendee(false);
      setSelectedAttendeeType('');
      setSelectedEmployee(null);
      onChange?.();
      onRefresh();
    } catch (err) {
      showAlert('Failed to add attendee', 'error', err);
    }
  };

  const handleCloseAddAttendeeModal = () => {
    setShowAddAttendee(false);
    setSelectedAttendeeType('');
    setSelectedEmployee(null);
  };

  const handleRemoveAttendee = (attendee) => {
    setAttendeeToRemove(attendee);
  };

  const handleConfirmRemoveAttendee = async () => {
    if (!attendeeToRemove) return;
    try {
      await meetingsApi.removeAttendee(meeting.id, attendeeToRemove.employee?.id);
      showAlert('Attendee removed', 'success');
      onChange?.();
      onRefresh();
    } catch (err) {
      showAlert('Failed to remove attendee', 'error', err);
    } finally {
      setAttendeeToRemove(null);
    }
  };

  const handleCancelRemoveAttendee = () => {
    setAttendeeToRemove(null);
  };

  const handleToggleMeetingType = async (typeId) => {
    if (!permissions.canEditGeneralInfo) return;

    // Calculate new type IDs
    const newTypeIds = selectedTypeIds.includes(typeId)
      ? selectedTypeIds.filter(id => id !== typeId)
      : [...selectedTypeIds, typeId];

    // Optimistic update
    setSelectedTypeIds(newTypeIds);

    try {
      await meetingsApi.updateMeeting(meeting.id, { meetingTypeIds: newTypeIds });
      onChange?.();
    } catch (err) {
      // Revert on error
      setSelectedTypeIds(selectedTypeIds);
      showAlert('Failed to update meeting types', 'error', err);
    }
  };

  const existingAttendeeIds = meeting.attendees?.map(a => a.employee?.id) || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Basic Info Section */}
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Basic Information
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Subject"
            value={meeting.subject || ''}
            onChange={(e) => onFieldChange('subject', e.target.value)}
            disabled={!permissions.canEditGeneralInfo}
          />

          <TextField
            fullWidth
            label="Description"
            value={meeting.description || ''}
            onChange={(e) => onFieldChange('description', e.target.value)}
            multiline
            rows={3}
            disabled={!permissions.canEditGeneralInfo}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <DateTimePicker
              label="Scheduled Date & Time"
              value={meeting.scheduledAt ? dayjs(meeting.scheduledAt) : null}
              onChange={(value) => onFieldChange('scheduledAt', value?.toISOString())}
              disabled={!permissions.canEditGeneralInfo}
              slotProps={{
                textField: {
                  fullWidth: true,
                }
              }}
            />

            <MeetingStatusSelector
              currentStatusId={meeting.currentStatus?.id || meeting.status?.id}
              onStatusChange={(newStatusId) => onStatusChange(newStatusId)}
              disabled={!permissions.canUpdateStatus}
              label="Status"
            />
          </Box>
        </Box>
      </Box>

      {/* Meeting Types Section */}
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Meeting Types
        </Typography>
        <MeetingTypesSelector
          selectedTypeIds={selectedTypeIds}
          onToggle={handleToggleMeetingType}
          disabled={!permissions.canEditGeneralInfo}
          helperText={permissions.canEditGeneralInfo
            ? 'Click to toggle meeting types.'
            : 'Meeting types assigned to this meeting.'}
        />
      </Box>

      {/* Participants Section */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Participants ({meeting.attendees?.length || 0})
          </Typography>
          {permissions.canEditGeneralInfo && (
            <HeaderButton onClick={() => setShowAddAttendee(true)}>
              <FaPlus /> Add Participant
            </HeaderButton>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {meeting.attendees?.map((attendee) => (
            <Box
              key={attendee.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                backgroundColor: '#f8fafc',
                borderRadius: 1,
                border: '1px solid #e2e8f0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <EmployeeAvatar employee={attendee.employee} size={40} />
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    {attendee.employee?.fullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {attendee.employee?.email}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={attendee.attendeeType?.name || 'Participant'}
                  size="small"
                  sx={{
                    backgroundColor: attendee.attendeeType?.color || '#E5E7EB',
                    color: attendee.attendeeType?.color ? '#fff' : '#374151',
                  }}
                />
                {permissions.canEditGeneralInfo && attendee.attendeeType?.code !== MEETING_ATTENDEE_TYPES.OWNER && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveAttendee(attendee)}
                    sx={{ color: 'error.main' }}
                  >
                    <FaTimes />
                  </IconButton>
                )}
              </Box>
            </Box>
          ))}
          {(!meeting.attendees || meeting.attendees.length === 0) && (
            <EmptyState title="No participants added yet" />
          )}
        </Box>
      </Box>

      {/* Remove Attendee Confirmation Modal */}
      <ConfirmationModal
        isOpen={Boolean(attendeeToRemove)}
        onClose={handleCancelRemoveAttendee}
        onConfirm={handleConfirmRemoveAttendee}
        title="Remove Participant"
        message={`Are you sure you want to remove ${attendeeToRemove?.employee?.fullName} from this meeting?`}
        actionButtonText="Remove"
        actionButtonColor="#dc2626"
      />

      {/* Add Participant Modal */}
      <Modal
        isOpen={showAddAttendee}
        onClose={handleCloseAddAttendeeModal}
        onSubmit={(e) => {
          e.preventDefault();
          handleAddAttendee();
        }}
        title="Add Participant"
        submitText="Save"
        cancelText="Cancel"
        submitDisabled={!selectedAttendeeType || !selectedEmployee}
        width="500px"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <FormControl fullWidth>
            <InputLabel>Attendee Type</InputLabel>
            <Select
              value={selectedAttendeeType}
              onChange={(e) => setSelectedAttendeeType(e.target.value)}
              label="Attendee Type"
            >
              {attendeeTypes
                .filter(t => t.code !== MEETING_ATTENDEE_TYPES.OWNER)
                .map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <EmployeeSearch
            value={selectedEmployee}
            onChange={setSelectedEmployee}
            placeholder="Search for employees..."
            disabledEmployeeIds={existingAttendeeIds}
            disabled={!selectedAttendeeType}
            height="56px"
            showSelectedAvatar
            sx={{ width: '100%' }}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default GeneralTab;
